import { getTokenExpirationApiCall, refreshTokenApiCall } from "./apicall/tokenApi";

interface AuthToken {
  t: string; // Token
  exp: number; // Expiration timestamp
  e: string; // Email or other identifier
  n: string; // Name
  r: string; // Role as '1' (admin), '2' (unitadmin), or empty string
}

const TOKEN_KEY = 'auth_token';

export const authService = {
  /**
   * Save the full token data to localStorage.
   * @param tokenData - Full token data object
   */
  setToken(tokenData: AuthToken): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokenData));
  },

  /**
   * Retrieve the full token data from localStorage.
   * @returns Parsed token data or null if not found.
   */
  getToken(): AuthToken | null {
    const storedData = localStorage.getItem(TOKEN_KEY);
    if (!storedData) return null;
    try {
      return JSON.parse(storedData);
    } catch (error) {
      console.error('Failed to parse token data:', error);
      return null;
    }
  },

  /**
   * Retrieve user information.
   * Converts role to human-readable form ('admin', 'unitadmin', or '').
   * @returns User info object or null if not available.
   */
  getUserInfo(): { email: string; name: string; role: string } | null {
    const tokenData = this.getToken();
    if (!tokenData) return null;

    let role = '';
    if (tokenData.r === '1') {
      role = 'admin';
    } else if (tokenData.r === '2') {
      role = 'unitadmin';
    }

    return {
      email: tokenData.e,
      name: tokenData.n,
      role,
    };
  },

  /**
   * Retrieve the user's primary role.
   * Converts role to 'admin', 'unitadmin', or ''.
   * @returns Human-readable role or empty string for guests.
   */
  getPrimaryRole(): string {
    const tokenData = this.getToken();
    if (!tokenData) return '';

    if (tokenData.r === '1') {
      return 'admin';
    } else if (tokenData.r === '2') {
      return 'unitadmin';
    }
    return ''; // Default for guests or unknown roles
  },

  /**
   * Save user information.
   * Maps human-readable roles (admin, unitadmin) to internal values (1, 2).
   * @param userInfo - Object containing user info (email, name, role)
   */
  setUserInfo(userInfo: { email: string; name: string; role: string }): void {
    const currentData = this.getToken() || { t: '', exp: 0 };

    // Map human-readable roles to internal values
    let roleValue = '';
    if (userInfo.role === 'admin') {
      roleValue = '1';
    } else if (userInfo.role === 'unitadmin') {
      roleValue = '2';
    }

    this.setToken({
      ...currentData,
      e: userInfo.email,
      n: userInfo.name,
      r: roleValue,
    });
  },

  /**
   * Save the raw token value.
   * @param token - Raw token string
   */
  setRawToken(token: string): void {
    const currentData = this.getToken() || { exp: 0, e: '', n: '', r: '' };

    this.setToken({
      ...currentData,
      t: token,
    });
  },

  /**
   * Retrieve the raw token value.
   * @returns The token string or null if not available.
   */
  getRawToken(): string | null {
    return this.getToken()?.t || null;
  },

  /**
   * Retrieve the expiration timestamp.
   * @returns Expiration timestamp or null if not available.
   */
  getTokenExpiration(): number | null {
    return this.getToken()?.exp || null;
  },

  /**
   * Save the expiration timestamp.
   * @param expiration - Expiration timestamp
   */
  setTokenExpiration(expiration: number): void {
    const currentData = this.getToken() || { t: '', e: '', n: '', r: '' };
   
    const expirationTimestamp = new Date(expiration).getTime();

    this.setToken({
      ...currentData,
      exp: expirationTimestamp,
    });
  },

  /**
   * Check if the token is expired.
   * @returns True if the token is expired, otherwise false.
   */
  isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) return true;
    return Date.now() > expiration;
  },

  /**
   * Check if the user has a specific role.
   * @param role - Role to check.
   * @returns True if the user has the role, otherwise false.
   */
  hasRole(role: string): boolean {
    const userInfo = this.getUserInfo();
    if (!userInfo) return false;

    return userInfo.role === role;
  },

  /**
   * Clear all authentication-related data from localStorage.
   */
  clearToken(): void {
    localStorage.removeItem(TOKEN_KEY);
  },


  async refreshAuthToken(): Promise<number | null> {
    try {
      const response = await refreshTokenApiCall(); // Replace with actual API call
      const newExpiration = new Date(response.data.expires_at).getTime();

      authService.setTokenExpiration(response.data.expires_at );

      return newExpiration;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  },


  async getRefreshAuthToken(): Promise<number | null> {
    try {
      const response = await getTokenExpirationApiCall(); // Replace with actual API call
      const newExpiration = new Date(response.data.expires_at).getTime();

      authService.setTokenExpiration(response.data.expires_at );

      return newExpiration;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  },
};


